import { Model } from 'backbone';
import Storage from '../../utils/Storage';
import { SaveMultipartFormData } from '../../utils';
import validate from 'validate.js';
import { userRules } from '../../validation_rules';
import _ from 'lodash';
import Radio from "backbone.radio";
const userChannel = Radio.channel('userModals');

const
	moment = require('moment'),
	saveMultipartFormData = new SaveMultipartFormData();

/**
 * This is User Model Class
 */
class UserModel extends Model {
	defaults() {
		return {
			errors: {},
			avatar_url: ''
		}
	}

	initialize() {
		/**
		 * Update Class data on initialize
		 */
		this.set(this.user);
		this.setUserAvatar();

		this.listenTo(this, 'request:update:success', user => {
			this.user = user;
			this.setUserAvatar();
		});
	}

	get user() {
		return Storage.get('user');
	}

	set user(user_data) {
		this.clear().set(user_data);

		Storage.set('user', user_data);
	}

	get is_adult(){
		if (!this.user) {
			return true;
		}
		const
			now = moment(),
			user_birthday = moment(this.user.birthday);

		return now.diff(user_birthday, 'years') >= 18;
	}

	get trial_days_to_end() {
		if (!this.with_trial || !this.get('trial_end')) {
			return undefined;
		}

		const
			minute = 1000 * 60,
			hour = minute * 60,
			day = hour * 24,

			now = Date.now(),
			trial_end = new Date(this.get('trial_end'));

		return Math.ceil((trial_end - now) / day);
	}

	get is_coach_soon_expired() {
		if (!this.trial_days_to_end) {
			return false;
		}

		return this.trial_days_to_end === 3;
	}

	get is_coach_trial_end() {
		if (_.isUndefined(this.trial_days_to_end)) {
			return false;
		}

		return this.trial_days_to_end <= 0;
	}

	get has_assistant_coach() {
		if(this.with_trial) {
			return !this.is_coach_trial_end;
		}

		return this.get('assistant_coach') === 1;
	}

	get with_trial() {
		const is_trial_plan = this.get('assistant_coach_plan') === 'trial';

		return is_trial_plan && Boolean(this.get('trial_end'));
	}

	isGameRecovered() {
		return this.get(process.env.GAME_RECOVERED_USER_KEY) === 1;
	}

	url() {
		return this.methodUrl;
	}

	update() {
		this.url = process.env.USER_QUERY_PATH;

		return this.getData({ type: 'request:update' });
	}

	setUserAvatar() {
		const
			avatar_path = process.env.AVATARS_PATH + this.get('id'),
			avatar_url = `${avatar_path}.png?v=${this.get('avatar_version')}`;

		this.set({
			avatar_url
		})
	}

	async loginWithNRLAuthCode({ code, redirect_url }) {
		this.url = 'nrlaccount/auth_code';

		this.user = {
			identity_token: null
		};

		const { success, result, errors } = await this.save(
			null,
			{
				data: {
					code,
					redirect_url
				}
			});

		if (!success) {
			this.trigger('user:login:fail', errors);
			return;
		}

		this.user = {
			identity_token: result.identity_token
		};

		await this.login({
			identity_token: this.user.identity_token
		});
	}

	async loginWithVerifyToken({ token }) {
		this.url = 'auth/verify_token';

		this.user = {
			identity_token: null
		};

		const { success, result, errors } = await this.save(
			null,
			{
				data: {
					token,
				}
			});

		if (!success) {
			this.trigger('user:login:fail', errors);
			return;
		}

		this.user = result.user;

		Storage.set('sid', result.sid);

		this.trigger('user:logged:in');
	}

	async checkNRLAccountUserHasExistingFanhubAccount() {
		this.url = 'nrlaccount/email_check';

		const identity_token = this.user.identity_token;

		const { success, result, errors } = await this.fetch({
			data: {
				identity_token
			}
		});

		if (!success) {
			this.trigger('user:checkNRLAccountUserHasExistingFanhubAccount:fail', errors);
			return;
		}

		this.trigger('user:checkNRLAccountUserHasExistingFanhubAccount:success', result);
	}

	async login(credentials) {
		this.url = process.env.AUTH_LOGIN_PART;

		const { success, result, errors } = await this.save(null, {
			data: credentials
		});

		if (!success) {
			this.trigger('user:login:fail', errors);
			return;
		}

		this.saveUser(result);
	}

	async logout() {
		this.url = process.env.AUTH_LOGOUT_PART;

		const { success, errors } = await this[process.env.AUTH_LOGOUT_METHOD](null, { data: {} });

		if (!success) {
			this.trigger('user:logout:fail', errors);
			return;
		}

		this.removeUser();
	}

	saveUser(result) {
		this.user = result.user;

		Storage.set('sid', result.session_id);

		this.trigger('user:logged:in');
	}

	removeUser() {
		this.user = { id: null };

		Storage.clear();

		this.redirectToNRLAuthLogout();
	}

	async create(data) {
		this.url = 'user/create';

		const { result } = await this.save(null, {
			data
		});

		this.saveUser(result);
		this.trigger('user:create:success');
		userChannel.trigger('hide:onboarding:modal');
	}

	async passwordReset(data) {
		this.url = 'auth/forgot_password';

		const { success, errors } = await this.fetch({
			data
		});

		if (!success) {
			this.trigger('user:password_reset:fail', errors);
			return;
		}

		this.trigger('user:password_reset:success');
	}

	async changePassword(data) {
		this.url = 'auth/change_password';

		const { success, result, errors } = await this.fetch({
			data
		});

		if (!success) {
			this.trigger('user:change_password:fail', errors);
			return;
		}

		this.trigger('user:change_password:success', result);
	}

	async checkTeamName(team_names) {
		this.url = 'user/check_team_name';

		return await this.fetch({
			data: {
				...team_names
			}
		});
	}

	async activateUser(data = {}) {
		this.url = 'user/activate';

		console.log('activateUser: before send:', data);

		const { success, result, errors } = await this.save(null, { data });

		console.log('success:', success, 'result:', result, 'errors:', errors);

		if (!success) {
			this.trigger('user:activate:fail', errors);
			return;
		}

		this.set({
			[process.env.GAME_RECOVERED_USER_KEY]: 1
		});

		this.user = this.toJSON();

		this.trigger('user:activate:success', result);
	}

	async recoverUser(data = {}) {
		this.url = 'user/recover';

		console.log('activateUser: before send:', data);

		const { success, result, errors } = await this.save(null, { data });

		console.log('success:', success, 'result:', result, 'errors:', errors);

		if (!success) {
			this.trigger('user:activate:fail', errors);
			return;
		}

		this.set({
			[process.env.GAME_RECOVERED_USER_KEY]: 1
		});

		this.user = this.toJSON();

		this.trigger('user:activate:success', result);
	}

	async updateUserData(formData = this.toJSON()) {
		const
			url = 'user/update',
			data = {
				url,
				formData
			};

		await saveMultipartFormData
			.save(data)
			.then(response => {
				if (response.errors.length) {
					this.trigger('user:update:errors', response.errors);
				}
				else {
					this.set(response.result);
					Storage.set('user', this.toJSON());
					this.trigger('user:update:success', response);
					this.setUserAvatar();
				}
			});

		return this;
	}

	redirectToNRLAuthLogout() {
		const nrl_auth_logout_url =
			process.env.NRL_URL +
			'/account/logout?post_logout_redirect_uri=' +
			process.env.ABSPATH
		;
		location.assign(nrl_auth_logout_url);
	}

	validateForm() {
		let user_error = this.toJSON(),
			errors = validate(
				user_error,
				userRules
			);

		if (!_.isUndefined(errors)) {
			this.trigger('user:update:form:errors', errors);
		}
		else {
			this.trigger('user:update:form:success');
		}

		return this;
	}

	async preregistration(data) {
		this.url = 'preregistration';

		const { success, errors } = await this.save(null, {
			data
		});

		if (!success) {
			this.trigger('user:preregistration:fail', errors);
			return;
		}
		this.trigger('user:preregistration:success', errors);
	}
}

export default new UserModel();